import { useTranslation } from 'react-i18next';

import { Card, CardHeader } from '@/app/components/ui/Card';
import { ProgressBar } from '@/app/components/ui/ProgressBar';
import { Skeleton } from '@/app/components/ui/Skeleton';
import type { FunnelStep } from '@/features/participation/api';
import { FUNNEL_STEP_ORDER } from '@/features/participation/metrics';
import { formatNumber } from '@/lib/format';

/**
 * The funnel card: six steps in order, each as a bar whose width is its
 * share of `invited` (the funnel's first step), with a headcount and (from
 * the second step on) its percentage of the *previous* step as a small
 * badge — two different percentages shown side by side on purpose, since
 * "share of everyone invited" and "conversion from the step before" answer
 * different questions. `needs_reactivation` stays a side branch, not a
 * further narrowing, since `ParticipationCalculator` computes it off
 * `activated`, not off `active_30d` (see that class's doc-comment).
 */
export function FunnelCard({ funnel }: { funnel: FunnelStep[] | undefined }) {
  const { t } = useTranslation();
  const invited = funnel?.find((s) => s.key === 'invited')?.count ?? 0;

  return (
    <Card>
      <CardHeader title={t('participation.funnel.title')} />
      {funnel === undefined ? (
        <Skeleton kind="table" />
      ) : (
        <ol className="flex flex-col gap-4" data-testid="participation-funnel">
          {FUNNEL_STEP_ORDER.map((key) => {
            const step = funnel.find((s) => s.key === key);
            const isBranch = key === 'needs_reactivation';
            const pctOfInvited = invited > 0 ? ((step?.count ?? 0) / invited) * 100 : 0;
            return (
              <li
                key={key}
                className={
                  isBranch
                    ? 'border-ink/20 flex flex-col gap-1.5 border-t border-dashed pt-4'
                    : 'flex flex-col gap-1.5'
                }
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-ink text-sm">{t(`participation.funnel.steps.${key}`)}</span>
                  <span className="flex items-baseline gap-2">
                    <span className="font-heading text-ink text-xl font-semibold">
                      {formatNumber(step?.count ?? 0)}
                    </span>
                    {step?.pct_of_previous !== null && step?.pct_of_previous !== undefined && (
                      <span className="text-accent text-xs">
                        {t('participation.funnel.pctOfPrevious', {
                          pct: formatNumber(step.pct_of_previous, { maximumFractionDigits: 1 }),
                        })}
                      </span>
                    )}
                  </span>
                </div>
                <ProgressBar pct={pctOfInvited} />
              </li>
            );
          })}
        </ol>
      )}
    </Card>
  );
}
